// ════════════════════════════════════════════════════════════════════
// UNDERWRITING AGENT VIEW — PDF Prompt 4.
//
// Two sections:
//   1. Client profile form (age, build, tobacco, conditions free-text)
//      → POSTs /api/uw/agent which resolves + evaluates + composes
//      a plain-English placement briefing.
//   2. Ranked offers + declines table (raw_offers / raw_declines from
//      the engine) so the operator can scan beyond the AI prose.
//
// Includes a "Seed rules from JSON" admin tool — until the PDF parser
// is built, this is how the operator imports curated grids.
// ════════════════════════════════════════════════════════════════════

import React from 'react';
import { readPersistedJwtDirect } from '../lib/authedFetch';
import { Sparkles, AlertCircle, CheckCircle, AlertTriangle } from 'lucide-react';
import { supabase } from '../lib/supabase';

async function apiFetch(path, opts = {}) {
  let jwt = null;
  jwt = readPersistedJwtDirect();
  const res = await fetch(path, {
    ...opts,
    headers: {
      'content-type': 'application/json',
      ...(jwt ? { Authorization: `Bearer ${jwt}` } : {}),
      ...(opts.headers || {}),
    },
  });
  const ct = res.headers.get('content-type') || '';
  const body = ct.includes('application/json') ? await res.json() : await res.text();
  if (!res.ok) {
    const msg = (body && body.error) || (typeof body === 'string' ? body.slice(0, 200) : `HTTP ${res.status}`);
    throw new Error(msg);
  }
  return body;
}

const EXAMPLES = [
  { label: '63yo, 5\'10" 215lb, no tobacco — diabetes 5yrs',
    grid: 'Final Expense', age: 63, height_in: 70, weight_lb: 215, tobacco: 'none',
    question: 'Diabetes for 5 years, on metformin only, no complications.' },
  { label: '55yo, lost a leg to diabetes, on oxygen',
    grid: 'Final Expense', age: 55, height_in: 68, weight_lb: 190, tobacco: 'none',
    question: 'Lost a leg, on oxygen, sugar diagnosed 10 years ago' },
  { label: '40yo healthy, term shopper',
    grid: 'Term', age: 40, height_in: 71, weight_lb: 180, tobacco: 'none',
    question: 'No conditions. Looking for term life.' },
];

export default function UnderwritingAgentView({ currentUser, showToast = () => {}, clientId = null }) {
  const [grid, setGrid]         = React.useState('Final Expense');
  const [age, setAge]           = React.useState('');
  const [heightIn, setHeightIn] = React.useState('');
  const [weightLb, setWeightLb] = React.useState('');
  const [tobacco, setTobacco]   = React.useState('none');
  const [question, setQuestion] = React.useState('');
  const [busy, setBusy]         = React.useState(false);
  const [error, setError]       = React.useState(null);
  const [result, setResult]     = React.useState(null);
  const [loadedClient, setLoadedClient] = React.useState(null);

  // Fix Gap #2 from 2026-07-14 lifecycle backtest: prior version made
  // the operator re-type medical conditions every time the UW view
  // opened, even when client_medical_profile.medical_conditions was
  // already stored. Now when we're passed a clientId (from a client
  // card's "Underwrite" button), auto-pull the profile and pre-fill
  // conditions + tobacco + age.
  React.useEffect(() => {
    if (!clientId) return;
    let cancelled = false;
    (async () => {
      try {
        const r = await apiFetch(`/api/health/client-medical-profile?client_id=${encodeURIComponent(clientId)}`);
        if (cancelled) return;
        if (r?.profile) {
          const conds = Array.isArray(r.profile.medical_conditions) ? r.profile.medical_conditions : [];
          if (conds.length > 0) {
            setQuestion(conds.map(c => c.note ? `${c.condition} (${c.note})` : c.condition).join(', '));
          }
        }
        if (r?.client) {
          setLoadedClient({ id: r.client.id, name: r.client.name || r.client.full_name || 'Client' });
          if (r.client.dob) {
            const ageYears = Math.floor((Date.now() - new Date(r.client.dob).getTime()) / (365.25 * 86400_000));
            if (ageYears > 0 && ageYears < 120) setAge(String(ageYears));
          }
          if (r.client.tobacco_status) setTobacco(r.client.tobacco_status);
        }
      } catch { /* non-fatal — form still works with manual entry */ }
    })();
    return () => { cancelled = true; };
  }, [clientId]);

  const runExample = (ex) => {
    setGrid(ex.grid);
    setAge(String(ex.age));
    setHeightIn(String(ex.height_in));
    setWeightLb(String(ex.weight_lb));
    setTobacco(ex.tobacco);
    setQuestion(ex.question);
  };

  const ask = async () => {
    if (busy) return;
    if (!question.trim()) { showToast('Type the conditions (or "none") first.'); return; }
    setBusy(true); setError(null); setResult(null);
    try {
      const json = await apiFetch('/api/uw/agent', {
        method: 'POST',
        body: JSON.stringify({
          question,
          grid,
          age:       age       ? Number(age)       : null,
          height_in: heightIn  ? Number(heightIn)  : null,
          weight_lb: weightLb  ? Number(weightLb)  : null,
          tobacco,
        }),
      });
      setResult(json);
    } catch (err) {
      setError(String(err.message || err));
    } finally {
      setBusy(false);
    }
  };

  const offers   = result?.raw?.evaluate?.offers   || [];
  const declines = result?.raw?.evaluate?.declines || [];
  const unresolved = result?.raw?.resolve?.unresolved || [];

  return (
    <div className="space-y-4">
      <div className="bg-gradient-to-br from-slate-900 to-slate-700 text-white rounded-xl p-5">
        <div className="text-amber-300 text-[0.55rem] font-black uppercase tracking-[0.3em]">Underwriting Agent</div>
        <h1 className="text-xl font-bold mt-1">Carrier Placement · Field-Grid Lookup</h1>
        {loadedClient && (
          <div className="mt-2 inline-flex items-center gap-2 bg-emerald-500/20 border border-emerald-400/40 rounded-md px-3 py-1 text-xs text-emerald-100">
            <span>✓</span>
            <span>Auto-filled from <strong>{loadedClient.name}</strong>'s medical profile</span>
          </div>
        )}
        <p className="text-slate-300 text-sm mt-2">
          Type the client's age, build, tobacco use, and their conditions in plain English ("sugar, on oxygen, mini stroke last year").
          The agent resolves what you typed to the grid's canonical conditions and returns which carrier-products will likely accept
          the client and at what offer level. <strong>Decision-support only — final underwriting is the carrier's.</strong>
        </p>
      </div>

      {/* Quick examples */}
      <div className="flex items-center gap-2 flex-wrap">
        {EXAMPLES.map((ex, i) => (
          <button
            key={i}
            onClick={() => runExample(ex)}
            className="text-xs text-slate-700 bg-white border border-slate-200 hover:border-slate-400 rounded-md px-2.5 py-1.5"
          >{ex.label}</button>
        ))}
      </div>

      {/* Intake form */}
      <div className="bg-white border border-slate-200 rounded-xl p-5 space-y-3">
        <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
          <label className="text-xs text-slate-600">
            <span className="block font-bold uppercase tracking-wider text-[10px] mb-1">Grid</span>
            <select value={grid} onChange={e => setGrid(e.target.value)} className="w-full text-sm border border-slate-300 rounded-md px-2 py-1.5 bg-white">
              <option value="Final Expense">Final Expense</option>
              <option value="Term">Term</option>
            </select>
          </label>
          <label className="text-xs text-slate-600">
            <span className="block font-bold uppercase tracking-wider text-[10px] mb-1">Age</span>
            <input value={age} onChange={e => setAge(e.target.value.replace(/[^0-9]/g, ''))} placeholder="63" className="w-full text-sm border border-slate-300 rounded-md px-2 py-1.5"/>
          </label>
          <label className="text-xs text-slate-600">
            <span className="block font-bold uppercase tracking-wider text-[10px] mb-1">Height (in)</span>
            <input value={heightIn} onChange={e => setHeightIn(e.target.value.replace(/[^0-9]/g, ''))} placeholder="70" className="w-full text-sm border border-slate-300 rounded-md px-2 py-1.5"/>
          </label>
          <label className="text-xs text-slate-600">
            <span className="block font-bold uppercase tracking-wider text-[10px] mb-1">Weight (lb)</span>
            <input value={weightLb} onChange={e => setWeightLb(e.target.value.replace(/[^0-9]/g, ''))} placeholder="215" className="w-full text-sm border border-slate-300 rounded-md px-2 py-1.5"/>
          </label>
          <label className="text-xs text-slate-600">
            <span className="block font-bold uppercase tracking-wider text-[10px] mb-1">Tobacco</span>
            <select value={tobacco} onChange={e => setTobacco(e.target.value)} className="w-full text-sm border border-slate-300 rounded-md px-2 py-1.5 bg-white">
              <option value="none">None</option>
              <option value="cigar_occasional">Cigar (occasional)</option>
              <option value="nicotine_any">Any nicotine</option>
            </select>
          </label>
        </div>
        <label className="block text-xs text-slate-600">
          <span className="block font-bold uppercase tracking-wider text-[10px] mb-1">Conditions (plain English)</span>
          <textarea
            value={question}
            onChange={e => setQuestion(e.target.value)}
            placeholder='e.g. "lost both legs from diabetes, on oxygen, mini stroke 2 years ago"'
            rows={3}
            className="w-full text-sm border border-slate-300 rounded-md px-2 py-2"
          />
        </label>
        <div className="flex justify-end">
          <button
            onClick={ask}
            disabled={busy || !question.trim()}
            className="bg-slate-900 hover:bg-slate-800 disabled:opacity-50 text-white text-sm font-bold px-5 py-2 rounded-md inline-flex items-center gap-2"
          >
            <Sparkles size={14}/> {busy ? 'Evaluating…' : 'Find carriers'}
          </button>
        </div>
      </div>

      {error && (
        <div className="bg-rose-50 border border-rose-200 rounded-xl p-4 text-sm text-rose-800">{error}</div>
      )}

      {result?.answer && (
        <div className="bg-white border border-slate-200 rounded-xl p-5">
          <div className="text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-2">
            AI Placement Briefing · {result.grid} grid ({result.grid_version})
          </div>
          <div className="text-sm text-slate-900 whitespace-pre-wrap">{result.answer}</div>
        </div>
      )}

      {unresolved.length > 0 && (
        <div className="bg-amber-50 border border-amber-300 rounded-xl p-4">
          <div className="flex items-center gap-2 text-amber-900 font-bold text-sm mb-2">
            <AlertTriangle size={14}/> No grid match for:
          </div>
          <ul className="text-xs text-amber-900 space-y-1">
            {unresolved.map((u, i) => <li key={i}>"{u.raw_text}" — {u.hint}</li>)}
          </ul>
        </div>
      )}

      {/* Raw offers + declines tables for scanning beyond the prose */}
      {(offers.length > 0 || declines.length > 0) && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div className="bg-white border border-emerald-200 rounded-xl overflow-hidden">
            <div className="bg-emerald-50 px-4 py-2 border-b border-emerald-200">
              <div className="text-[10px] font-bold uppercase tracking-wider text-emerald-700 flex items-center gap-1.5">
                <CheckCircle size={12}/> Offers ({offers.length})
              </div>
            </div>
            {offers.length === 0 ? (
              <div className="px-4 py-3 text-sm text-slate-500 italic">No offers.</div>
            ) : (
              <div className="divide-y divide-slate-100">
                {offers.slice(0, 20).map((o, i) => (
                  <div key={i} className="px-4 py-2">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-bold text-slate-900 text-sm">{o.carrier}</span>
                      <span className="text-xs text-slate-600">{o.product}</span>
                      <span className="text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-700 border border-emerald-200">{o.outcome}</span>
                      {o.conditional && <span className="text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded bg-amber-100 text-amber-700 border border-amber-200">Check meds</span>}
                      {o.build_class && <span className="text-[10px] text-slate-500">build: {o.build_class}</span>}
                    </div>
                    {o.build_note && <div className="text-xs text-slate-500 mt-0.5">{o.build_note}</div>}
                    {(o.driving_conditions || []).filter(d => d.outcome !== 'NOT_ASKED').slice(0, 3).map((d, j) => (
                      <div key={j} className="text-[11px] text-slate-500 mt-0.5">
                        <span className="font-semibold">{d.condition}:</span> {d.outcome} <span className="italic">— {d.raw_text}</span>
                      </div>
                    ))}
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="bg-white border border-rose-200 rounded-xl overflow-hidden">
            <div className="bg-rose-50 px-4 py-2 border-b border-rose-200">
              <div className="text-[10px] font-bold uppercase tracking-wider text-rose-700 flex items-center gap-1.5">
                <AlertCircle size={12}/> Declines ({declines.length})
              </div>
            </div>
            {declines.length === 0 ? (
              <div className="px-4 py-3 text-sm text-slate-500 italic">No declines.</div>
            ) : (
              <div className="divide-y divide-slate-100">
                {declines.slice(0, 20).map((d, i) => (
                  <div key={i} className="px-4 py-2">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-bold text-slate-900 text-sm">{d.carrier}</span>
                      <span className="text-xs text-slate-600">{d.product}</span>
                    </div>
                    <div className="text-[11px] text-rose-700 mt-0.5">{d.disqualifying_condition}</div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Admin: seed rules from JSON */}
      <details className="bg-white border border-slate-200 rounded-xl p-4">
        <summary className="cursor-pointer text-xs font-bold uppercase tracking-wider text-slate-600">
          Admin · Bulk-seed rules from JSON
        </summary>
        <BulkSeed showToast={showToast}/>
      </details>
    </div>
  );
}

function BulkSeed({ showToast }) {
  const [json, setJson]     = React.useState('');
  const [grid, setGrid]     = React.useState('Final Expense');
  const [promote, setPromote] = React.useState(false);
  const [busy, setBusy]     = React.useState(false);
  const [result, setResult] = React.useState(null);

  const submit = async () => {
    if (busy) return;
    let parsed;
    try { parsed = JSON.parse(json); } catch { showToast('Not valid JSON.'); return; }
    if (!Array.isArray(parsed)) { showToast('Expected an array of rule objects.'); return; }
    setBusy(true);
    try {
      const out = await apiFetch('/api/uw/seed-rules', {
        method: 'POST',
        body: JSON.stringify({ grid, rules: parsed, promote_to_production: promote }),
      });
      setResult(out);
      showToast(`Seeded ${out.inserted?.rules || 0} rules across ${out.inserted?.products || 0} products. ${out.errors_count} errors.`);
    } catch (err) {
      showToast(`Seed failed: ${err.message || err}`);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mt-3 space-y-2">
      <div className="flex items-center gap-2">
        <select value={grid} onChange={e => setGrid(e.target.value)} className="text-sm border border-slate-300 rounded-md px-2 py-1.5 bg-white">
          <option value="Final Expense">Final Expense</option>
          <option value="Term">Term</option>
        </select>
        <label className="text-xs text-slate-600 inline-flex items-center gap-1.5">
          <input type="checkbox" checked={promote} onChange={e => setPromote(e.target.checked)}/>
          Promote to production (only if zero errors)
        </label>
      </div>
      <textarea
        value={json}
        onChange={e => setJson(e.target.value)}
        rows={10}
        placeholder='[\n  { "carrier": "Mutual of Omaha", "product": "Living Promise", "condition": "Cancer", "raw_text": "<2yrs DECLINE; >4yrs allowed", "clauses": [{"max_months":24,"outcome":"DECLINE"},{"min_months":48,"outcome":"ALLOWED"}] }\n]'
        className="w-full text-xs font-mono border border-slate-300 rounded-md px-2 py-2"
      />
      <button onClick={submit} disabled={busy || !json.trim()} className="bg-slate-900 hover:bg-slate-800 disabled:opacity-50 text-white text-xs font-bold uppercase tracking-wider px-3 py-1.5 rounded-md">
        {busy ? 'Seeding…' : 'Seed rules'}
      </button>
      {result && (
        <div className="text-xs text-slate-600 mt-2">
          Inserted: {JSON.stringify(result.inserted)} · Errors: {result.errors_count}
        </div>
      )}
    </div>
  );
}
